import { computed, reactive, ref, unref } from 'vue';
import { useRoute } from 'vue-router';
import ImovelAPI from 'dashboard/api/imoveis';
import { useAlert } from 'dashboard/composables';
import {
  actionErrorMessage,
  formatDateTime,
  identityConfidenceLabel,
  identitySourceLabel,
  normalizePayload,
} from '../utils/imovelShowFormatters';

/**
 * Estado, derivações e ações da identidade física canônica do imóvel.
 *
 * @param {object} options
 * @param {import('vue').Ref<object>} options.imovel imóvel camelizado do store
 * @param {import('vue').Ref<Array>} options.portalSyncs syncs normalizados
 */
export function useImovelIdentity({ imovel, portalSyncs }) {
  const route = useRoute();

  const identityPayload = ref(null);
  const identityLoading = ref(false);
  const identityError = ref('');
  const identityPermissionDenied = ref(false);
  const identitySuccessMessage = ref('');
  const aliasSaving = ref(false);
  const aliasError = ref('');
  const aliasForm = reactive({
    source: '',
    external_id: '',
    external_url: '',
  });

  const identityEnvelope = computed(() =>
    normalizePayload(identityPayload.value || unref(imovel)?.identity || {})
  );
  const identityCurrentAddress = computed(() =>
    normalizePayload(identityEnvelope.value?.currentAddress || {})
  );
  const identityAddressKeys = computed(() =>
    normalizePayload(identityCurrentAddress.value?.keys || {})
  );
  const canonicalAddressLine = computed(() => {
    const address = identityCurrentAddress.value;
    return [
      address.streetName || address.rawAddress,
      address.streetNumber,
      address.complement,
      address.neighborhood,
      address.city,
      address.state,
      address.postalCode,
    ]
      .filter(Boolean)
      .join(', ');
  });
  const hasIdentityAddress = computed(
    () =>
      Boolean(canonicalAddressLine.value) ||
      Boolean(identityCurrentAddress.value?.rawAddress)
  );
  const identityUsesLegacyProjection = computed(
    () => identityCurrentAddress.value?.source === 'legacy'
  );
  const identityAliases = computed(() =>
    Array.isArray(identityEnvelope.value?.aliases)
      ? identityEnvelope.value.aliases.map(alias => normalizePayload(alias))
      : []
  );
  const identityConflicts = computed(() =>
    Array.isArray(identityEnvelope.value?.duplicateCandidates)
      ? identityEnvelope.value.duplicateCandidates.map(candidate =>
          normalizePayload(candidate)
        )
      : []
  );
  const identityActionableConflicts = computed(() =>
    identityConflicts.value.filter(
      candidate =>
        candidate.requiresHumanReview ||
        ['pending', 'confirmed'].includes(candidate.status)
    )
  );
  const identityChangeHistory = computed(() =>
    Array.isArray(identityEnvelope.value?.changeHistory)
      ? identityEnvelope.value.changeHistory.map(entry =>
          normalizePayload(entry)
        )
      : []
  );
  const identityMergeHistory = computed(() =>
    Array.isArray(identityEnvelope.value?.mergeHistory)
      ? identityEnvelope.value.mergeHistory.map(entry =>
          normalizePayload(entry)
        )
      : []
  );
  const identitySourceLinks = computed(() => {
    const links = identityAliases.value.map(alias => ({
      key: `alias-${alias.id || alias.source}-${alias.externalId}`,
      kind: 'Alias externo',
      source: identitySourceLabel(alias.source),
      reference: alias.externalId,
      url: alias.externalUrl,
      status: alias.current === false ? 'Histórico' : 'Atual',
    }));

    const current = unref(imovel);
    if (current?.primaryListing?.id) {
      links.push({
        key: `primary-listing-${current.primaryListing.id}`,
        kind: 'Listing interno',
        source: 'Imudei',
        reference: `#${current.primaryListing.id}`,
        status: current.primaryListing.listingStatus,
      });
    }

    (unref(portalSyncs) || []).forEach(sync => {
      links.push({
        key: `portal-${sync.portal}-${sync.id || sync.externalRef || sync.remoteId}`,
        kind: 'Publicação',
        source: identitySourceLabel(sync.portal),
        reference: sync.remoteId || sync.externalRef,
        status: sync.status,
      });
    });

    return links;
  });
  const identityCanonicalRows = computed(() =>
    [
      {
        label: 'Origem vencedora',
        value: identitySourceLabel(identityCurrentAddress.value?.source),
      },
      {
        label: 'Confiança',
        value: identityConfidenceLabel(
          identityCurrentAddress.value?.confidence
        ),
      },
      { label: 'Chave do prédio', value: identityAddressKeys.value?.baseKey },
      { label: 'Chave da unidade', value: identityAddressKeys.value?.fullKey },
      {
        label: 'Chave do complemento',
        value: identityAddressKeys.value?.complementKey,
      },
      {
        label: 'Última normalização',
        value: identityCurrentAddress.value?.updatedAt
          ? formatDateTime(identityCurrentAddress.value.updatedAt)
          : '',
      },
    ].filter(row => row.value)
  );
  const identityEmpty = computed(
    () =>
      !identityLoading.value &&
      !identityError.value &&
      !identityPermissionDenied.value &&
      !hasIdentityAddress.value &&
      !identityAliases.value.length &&
      !identityConflicts.value.length &&
      !identityChangeHistory.value.length &&
      !identityMergeHistory.value.length &&
      !identitySourceLinks.value.length
  );
  const identityStatusLabel = computed(() => {
    if (identityLoading.value) return 'Verificando';
    if (identityPermissionDenied.value) return 'Sem permissão';
    if (identityError.value) return 'Erro';
    if (identityActionableConflicts.value.length) return 'Conflito pendente';
    if (identityUsesLegacyProjection.value) return 'Legado';
    if (!hasIdentityAddress.value) return 'Vazio';
    return 'Canônico';
  });
  const identityStatusTone = computed(() => {
    if (identityLoading.value) return 'info';
    if (identityPermissionDenied.value || identityError.value) return 'danger';
    if (
      identityActionableConflicts.value.length ||
      identityUsesLegacyProjection.value
    ) {
      return 'warning';
    }
    return hasIdentityAddress.value ? 'positive' : 'neutral';
  });
  const canSubmitAlias = computed(() =>
    Boolean(aliasForm.source.trim() && aliasForm.external_id.trim())
  );

  const fetchIdentity = async () => {
    if (!route.params.id) return;

    identityLoading.value = true;
    identityError.value = '';
    identitySuccessMessage.value = '';
    identityPermissionDenied.value = false;
    try {
      const { data } = await ImovelAPI.identity(route.params.id);
      identityPayload.value = normalizePayload(data);
    } catch (error) {
      if (error?.response?.status === 403) {
        identityPayload.value = null;
        identityPermissionDenied.value = true;
        identityError.value =
          'Você não tem permissão para visualizar a identidade física deste imóvel.';
        return;
      }

      identityPayload.value = unref(imovel)?.identity || identityPayload.value;
      identityError.value = actionErrorMessage(
        error,
        'Não foi possível carregar a identidade física.'
      );
    } finally {
      identityLoading.value = false;
    }
  };

  const clearAliasForm = () => {
    aliasForm.source = '';
    aliasForm.external_id = '';
    aliasForm.external_url = '';
  };

  const submitAlias = async () => {
    aliasError.value = '';
    identitySuccessMessage.value = '';
    if (!canSubmitAlias.value) {
      aliasError.value = 'Informe fonte e código externo.';
      return;
    }

    aliasSaving.value = true;
    try {
      const { data } = await ImovelAPI.createAlias(route.params.id, {
        source: aliasForm.source.trim(),
        external_id: aliasForm.external_id.trim(),
        external_url: aliasForm.external_url.trim() || undefined,
      });
      identityPayload.value = normalizePayload(data);
      clearAliasForm();
      identitySuccessMessage.value =
        'Alias externo registrado para revisão da identidade.';
      useAlert(identitySuccessMessage.value);
    } catch (error) {
      aliasError.value = actionErrorMessage(
        error,
        'Não foi possível registrar o alias externo.'
      );
    } finally {
      aliasSaving.value = false;
    }
  };

  return {
    identityPayload,
    identityLoading,
    identityError,
    identityPermissionDenied,
    identitySuccessMessage,
    aliasForm,
    aliasSaving,
    aliasError,
    canonicalAddressLine,
    hasIdentityAddress,
    identityUsesLegacyProjection,
    identityAliases,
    identityConflicts,
    identityActionableConflicts,
    identityChangeHistory,
    identityMergeHistory,
    identitySourceLinks,
    identityCanonicalRows,
    identityEmpty,
    identityStatusLabel,
    identityStatusTone,
    canSubmitAlias,
    fetchIdentity,
    clearAliasForm,
    submitAlias,
  };
}

export default useImovelIdentity;
